import React, { useState } from 'react';

/**
 * Feature Documentation Modal Component
 * Shows PDF-ready documentation for each analytics feature
 */

const FEATURE_DOCS = {
  pol: {
    title: 'Feature 1.1: Pattern of Life (POL)',
    subtitle: 'Behavioral Baseline Module',
    color: '#3B82F6',
    content: `
      <div class="section">
        <div class="section-title" style="background: #3B82F6;">WHAT IT DOES</div>
        <p>Pattern of Life (POL) establishes a <strong>behavioral baseline</strong> for each monitored individual by analyzing their location history. It identifies significant locations (home, work, frequent destinations) and builds a routine matrix showing when and where the person is expected to be throughout the week.</p>
      </div>
      <div class="two-col">
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #3B82F6;">SIGNIFICANT LOCATIONS</div>
            <table>
              <tr><th>Type</th><th>Description</th><th>Identification</th></tr>
              <tr><td>🏠 <strong>Home</strong></td><td>Primary residence</td><td>Highest nighttime dwell</td></tr>
              <tr><td>🏢 <strong>Work</strong></td><td>Employment location</td><td>Highest weekday daytime dwell</td></tr>
              <tr><td>📍 <strong>Frequent</strong></td><td>Regular destinations</td><td>Recurring visits ≥3x/week</td></tr>
              <tr><td>❓ <strong>Unknown</strong></td><td>Unclassified location</td><td>Insufficient data</td></tr>
            </table>
            <div style="margin-top: 10px;">
              <strong>Location Metrics:</strong>
              <ul style="margin-left: 15px; font-size: 10px;">
                <li><strong>Confidence Score:</strong> 0-100% certainty of classification</li>
                <li><strong>Visit Count:</strong> Total visits to location</li>
                <li><strong>Avg Dwell Time:</strong> Average time spent per visit</li>
                <li><strong>Radius:</strong> Geographic spread of activity (meters)</li>
              </ul>
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #3B82F6;">BUSINESS BENEFITS</div>
            <ul class="benefits-list">
              <li><strong>Establishes Baseline:</strong> Know what "normal" looks like for each person</li>
              <li><strong>Enables Deviation Detection:</strong> Identify when behavior changes</li>
              <li><strong>Residence Verification:</strong> Confirm reported address matches actual home</li>
              <li><strong>Employment Verification:</strong> Confirm work location and schedule</li>
              <li><strong>Supports Check-in Planning:</strong> Schedule visits when person likely home</li>
            </ul>
          </div>
        </div>
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #3B82F6;">DEVIATION CHECKER</div>
            <div class="metric-box" style="border-left-color: #3B82F6;">
              <div class="metric-title" style="color: #3B82F6;">How It Works</div>
              Compares a location against the POL model to determine if it's expected or unusual.
            </div>
            <table>
              <tr><th>Alert Level</th><th>Score</th><th>Meaning</th></tr>
              <tr style="background: #DCFCE7;"><td>None</td><td>0-30%</td><td>At expected location</td></tr>
              <tr style="background: #FEF3C7;"><td>Low</td><td>30-50%</td><td>Slightly unexpected</td></tr>
              <tr style="background: #FED7AA;"><td>Medium</td><td>50-70%</td><td>Moderately unusual</td></tr>
              <tr style="background: #FECACA;"><td>High</td><td>70-100%</td><td>Highly unexpected</td></tr>
            </table>
          </div>
          <div class="section">
            <div class="section-title" style="background: #3B82F6;">KEY ACTIONS</div>
            <div class="metric-box" style="border-left-color: #3B82F6;"><strong>Build POL Model:</strong> Creates model from location history (min 7 days data)</div>
            <div class="metric-box" style="border-left-color: #3B82F6;"><strong>Get POL Model:</strong> Retrieves existing model for review</div>
            <div class="metric-box" style="border-left-color: #3B82F6;"><strong>Check Deviation:</strong> Tests if a location is within normal patterns</div>
          </div>
        </div>
      </div>
    `,
  },
  anomaly: {
    title: 'Feature 1.2: Anomaly Detection',
    subtitle: 'Multi-Model Ensemble Detection',
    color: '#8B5CF6',
    content: `
      <div class="section">
        <div class="section-title" style="background: #8B5CF6;">WHAT IT DOES</div>
        <p>Anomaly Detection uses a <strong>three-model ensemble</strong> (Isolation Forest, AutoEncoder, Statistical) trained on each person's location history to detect unusual behavior. It identifies when someone is at an unexpected location, at an unexpected time, or moving in an unusual pattern.</p>
      </div>
      <div class="two-col">
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #8B5CF6;">ENSEMBLE MODELS</div>
            <div style="background: #F3E8FF; border: 1px solid #DDD6FE; border-radius: 4px; padding: 10px; margin-bottom: 10px;">
              <div style="text-align: center; margin-bottom: 8px;"><strong>Three-Model Voting System</strong></div>
              <div style="display: flex; justify-content: space-around; text-align: center; font-size: 10px;">
                <div><div style="background: #DBEAFE; padding: 3px 8px; border-radius: 3px; margin-bottom: 3px;">IF</div>Isolation Forest<br/><span style="color: #666;">Outlier detection</span></div>
                <div><div style="background: #FCE7F3; padding: 3px 8px; border-radius: 3px; margin-bottom: 3px;">AE</div>AutoEncoder<br/><span style="color: #666;">Reconstruction error</span></div>
                <div><div style="background: #FEF3C7; padding: 3px 8px; border-radius: 3px; margin-bottom: 3px;">Stat</div>Statistical<br/><span style="color: #666;">Distribution analysis</span></div>
              </div>
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #8B5CF6;">ANOMALY TYPES</div>
            <table>
              <tr><th>Type</th><th>Description</th><th>Example</th></tr>
              <tr><td><strong>Temporal</strong></td><td>Known location, wrong time</td><td>At work at 3am</td></tr>
              <tr><td><strong>Spatial</strong></td><td>Unexpected location</td><td>Never-visited city</td></tr>
              <tr><td><strong>Route</strong></td><td>Unusual travel path</td><td>Detour to unknown area</td></tr>
              <tr><td><strong>Behavioral</strong></td><td>Unusual movement</td><td>Erratic stops, circling</td></tr>
            </table>
          </div>
          <div class="section">
            <div class="section-title" style="background: #8B5CF6;">BUSINESS BENEFITS</div>
            <ul class="benefits-list">
              <li><strong>Real-time Alerts:</strong> Immediate notification of suspicious activity</li>
              <li><strong>Reduced False Positives:</strong> Ensemble approach filters noise</li>
              <li><strong>Personalized Detection:</strong> Models trained per individual</li>
              <li><strong>Objective Evidence:</strong> Data-backed anomaly documentation</li>
            </ul>
          </div>
        </div>
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #8B5CF6;">ALERT LEVELS</div>
            <table>
              <tr><th>Level</th><th>Score</th><th>Action Required</th></tr>
              <tr style="background: #DCFCE7;"><td><strong>None</strong></td><td>0-30%</td><td>Normal - no action</td></tr>
              <tr style="background: #FEF3C7;"><td><strong>Low</strong></td><td>30-50%</td><td>Monitor - note in file</td></tr>
              <tr style="background: #FED7AA;"><td><strong>Medium</strong></td><td>50-70%</td><td>Review within 24 hours</td></tr>
              <tr style="background: #FECACA;"><td><strong>High</strong></td><td>70-100%</td><td>Immediate investigation</td></tr>
            </table>
          </div>
          <div class="section">
            <div class="section-title" style="background: #8B5CF6;">KEY METRICS</div>
            <div class="metric-box" style="border-left-color: #8B5CF6;"><div class="metric-title" style="color: #8B5CF6;">Anomaly Score (0-100%)</div>Combined probability that the event is anomalous.</div>
            <div class="metric-box" style="border-left-color: #8B5CF6;"><div class="metric-title" style="color: #8B5CF6;">Confidence (0-100%)</div>How certain the models are. Low = need more data.</div>
            <div class="metric-box" style="border-left-color: #8B5CF6;"><div class="metric-title" style="color: #8B5CF6;">Model Agreement</div>Which models flagged it. Higher agreement = stronger signal.</div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #8B5CF6;">WHEN TO ESCALATE</div>
            <div style="background: #FEF2F2; border: 1px solid #FECACA; border-radius: 4px; padding: 8px; font-size: 10px;">
              <ul style="margin-left: 15px;">
                <li>Multiple High alerts within 24 hours</li>
                <li>Spatial anomaly near border/airport</li>
                <li>Pattern of increasing anomaly frequency</li>
                <li>Anomaly coincides with high-risk zone visit</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    `,
  },
  risk: {
    title: 'Feature 1.3: Risk Scoring',
    subtitle: 'Composite Risk Assessment',
    color: '#F97316',
    content: `
      <div class="section">
        <div class="section-title" style="background: #F97316;">WHAT IT DOES</div>
        <p>Risk Scoring synthesizes multiple behavioral signals into a single <strong>1.00-5.00 risk score</strong> that quantifies flight/compliance risk. The model weighs anomaly frequency, compliance history, pattern stability, and recency to produce an actionable metric for case prioritization.</p>
      </div>
      <div class="two-col">
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #F97316;">RISK SCORE SCALE</div>
            <div style="display: flex; height: 24px; border-radius: 10px; overflow: hidden; margin: 10px 0;">
              <div style="flex: 1; background: #22C55E; display: flex; align-items: center; justify-content: center; color: white; font-size: 9px;">1-2 Low</div>
              <div style="flex: 1; background: #F59E0B; display: flex; align-items: center; justify-content: center; color: white; font-size: 9px;">2-3 Medium</div>
              <div style="flex: 1; background: #F97316; display: flex; align-items: center; justify-content: center; color: white; font-size: 9px;">3-4 High</div>
              <div style="flex: 1; background: #EF4444; display: flex; align-items: center; justify-content: center; color: white; font-size: 9px;">4-5 Critical</div>
            </div>
            <table>
              <tr><th>Category</th><th>Score</th><th>Action</th></tr>
              <tr style="background: #DCFCE7;"><td><strong>Low Risk</strong></td><td>1.0-2.0</td><td>Standard monitoring</td></tr>
              <tr style="background: #FEF3C7;"><td><strong>Medium Risk</strong></td><td>2.0-3.0</td><td>Enhanced monitoring, monthly review</td></tr>
              <tr style="background: #FED7AA;"><td><strong>High Risk</strong></td><td>3.0-4.0</td><td>Weekly review, consider intervention</td></tr>
              <tr style="background: #FECACA;"><td><strong>Critical Risk</strong></td><td>4.0-5.0</td><td>Immediate supervisor review</td></tr>
            </table>
          </div>
          <div class="section">
            <div class="section-title" style="background: #F97316;">CONTRIBUTING FACTORS</div>
            <table style="font-size: 9px;">
              <tr><th>Factor</th><th>Weight</th><th>What It Measures</th></tr>
              <tr><td>Anomaly Frequency</td><td>30%</td><td>Rate of detected anomalies</td></tr>
              <tr><td>Compliance</td><td>30%</td><td>Rule violations, missed check-ins</td></tr>
              <tr><td>Pattern Stability</td><td>25%</td><td>Deviation from routines</td></tr>
              <tr><td>Recency</td><td>15%</td><td>Recent activity emphasis</td></tr>
            </table>
          </div>
        </div>
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #F97316;">TREND ANALYSIS</div>
            <div class="metric-box" style="border-left-color: #F97316;">
              <div class="metric-title" style="color: #F97316;">30-Day Trend Chart</div>
              <ul style="margin-left: 15px; font-size: 10px; margin-top: 5px;">
                <li><strong>↑ Increasing:</strong> Behavior deteriorating - investigate</li>
                <li><strong>→ Stable:</strong> Consistent risk level</li>
                <li><strong>↓ Decreasing:</strong> Improvement - document progress</li>
              </ul>
            </div>
            <div class="metric-box" style="border-left-color: #F97316;">
              <div class="metric-title" style="color: #F97316;">Model Confidence</div>
              How certain the model is (0-100%). Low confidence = insufficient data.
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #F97316;">BUSINESS BENEFITS</div>
            <ul class="benefits-list">
              <li><strong>Single Actionable Metric:</strong> One number to guide decisions</li>
              <li><strong>Resource Prioritization:</strong> Focus on highest-risk cases first</li>
              <li><strong>Objective Assessment:</strong> Data-driven, not subjective</li>
              <li><strong>Early Warning:</strong> Rising scores predict problems</li>
              <li><strong>Audit Trail:</strong> Documented reasoning for decisions</li>
            </ul>
          </div>
          <div class="section">
            <div class="section-title" style="background: #F97316;">WHEN TO ESCALATE</div>
            <div style="background: #FEF2F2; border: 1px solid #FECACA; border-radius: 4px; padding: 8px; font-size: 10px;">
              <ul style="margin-left: 15px;">
                <li>Score crosses into Critical (75+)</li>
                <li>Score increases by 15+ points in one week</li>
                <li>Multiple High-impact factors simultaneously</li>
                <li>Consistent increase over 2+ weeks</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    `,
  },
  zones: {
    title: 'Feature 1.4: High-Risk Zone Monitoring',
    subtitle: 'Geographic Risk Tracking',
    color: '#DC2626',
    content: `
      <div class="section">
        <div class="section-title" style="background: #DC2626;">WHAT IT DOES</div>
        <p>High-Risk Zone Monitoring tracks subject exposure to <strong>geographically defined danger areas</strong> such as gang territories, stash houses, smuggling corridors, and flight risk locations. It quantifies risk through visit frequency, dwell time, and zone severity.</p>
      </div>
      <div class="two-col">
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #DC2626;">ZONE CATEGORIES</div>
            <table>
              <tr><th>Category</th><th>Description</th></tr>
              <tr><td>🏚️ <strong>Known Stash House</strong></td><td>Drug/contraband storage</td></tr>
              <tr><td>⚠️ <strong>Gang Territory</strong></td><td>Documented gang activity</td></tr>
              <tr><td>🛤️ <strong>Smuggling Corridor</strong></td><td>Known smuggling routes</td></tr>
              <tr><td>🚔 <strong>Previous Arrest Site</strong></td><td>Past arrest locations</td></tr>
              <tr><td>👥 <strong>Criminal Associate</strong></td><td>Known associate locations</td></tr>
              <tr><td>🛂 <strong>Border Crossing</strong></td><td>Unmonitored border areas</td></tr>
              <tr><td>✈️ <strong>Flight Risk Location</strong></td><td>Airports, bus stations</td></tr>
            </table>
          </div>
          <div class="section">
            <div class="section-title" style="background: #DC2626;">RISK LEVELS (1-5)</div>
            <table>
              <tr><th>Level</th><th>Name</th><th>Indicator</th></tr>
              <tr style="background: #DCFCE7;"><td><strong>1</strong></td><td>Low</td><td style="color: #22C55E;">●</td></tr>
              <tr style="background: #FEF3C7;"><td><strong>2</strong></td><td>Guarded</td><td style="color: #F59E0B;">●</td></tr>
              <tr style="background: #FED7AA;"><td><strong>3</strong></td><td>Elevated</td><td style="color: #F97316;">●</td></tr>
              <tr style="background: #FECACA;"><td><strong>4</strong></td><td>High</td><td style="color: #EF4444;">●</td></tr>
              <tr style="background: #FCA5A5;"><td><strong>5</strong></td><td>Critical</td><td style="color: #991B1B;">●</td></tr>
            </table>
          </div>
        </div>
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #DC2626;">EXPOSURE SCORE</div>
            <div style="display: flex; height: 20px; border-radius: 8px; overflow: hidden; margin: 8px 0;">
              <div style="flex: 3; background: #22C55E; display: flex; align-items: center; justify-content: center; color: white; font-size: 8px;">Low &lt;30%</div>
              <div style="flex: 2; background: #F59E0B; display: flex; align-items: center; justify-content: center; color: white; font-size: 8px;">Mod 30-50%</div>
              <div style="flex: 2; background: #F97316; display: flex; align-items: center; justify-content: center; color: white; font-size: 8px;">High 50-70%</div>
              <div style="flex: 3; background: #EF4444; display: flex; align-items: center; justify-content: center; color: white; font-size: 8px;">Critical 70%+</div>
            </div>
            <div class="metric-box" style="border-left-color: #DC2626;">
              <div class="metric-title" style="color: #DC2626;">Calculation Factors</div>
              <table style="font-size: 9px; margin-top: 5px;">
                <tr><th>Factor</th><th>Weight</th></tr>
                <tr><td>Visit Frequency</td><td>40%</td></tr>
                <tr><td>Dwell Time</td><td>30%</td></tr>
                <tr><td>POL Overlap</td><td>30%</td></tr>
              </table>
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #DC2626;">WHEN TO ESCALATE</div>
            <div style="background: #FEF2F2; border: 1px solid #FECACA; border-radius: 4px; padding: 8px; font-size: 10px;">
              <ul style="margin-left: 15px;">
                <li>Any visit to Level 5 (Critical) zone</li>
                <li>Exposure score exceeds 70%</li>
                <li>Repeated visits to same high-risk zone</li>
                <li>Extended dwell time (>30 min) in Level 4+ zone</li>
                <li>New zone type appears (e.g., first airport visit)</li>
              </ul>
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #DC2626;">BUSINESS BENEFITS</div>
            <ul class="benefits-list">
              <li><strong>Geographic Risk Awareness:</strong> Know where danger exists</li>
              <li><strong>Proximity Alerts:</strong> Notification of zone entry</li>
              <li><strong>Association Detection:</strong> Identify connections via location</li>
              <li><strong>Flight Risk Assessment:</strong> Track airport/border proximity</li>
            </ul>
          </div>
        </div>
      </div>
    `,
  },
  clustering: {
    title: 'Feature 1.5: Behavioral Pattern Clustering',
    subtitle: 'K-Means Cohort Analysis',
    color: '#4F46E5',
    content: `
      <div class="section">
        <div class="section-title" style="background: #4F46E5;">WHAT IT DOES</div>
        <p>Pattern Clustering uses <strong>K-Means machine learning</strong> to automatically group monitored individuals into behavioral categories based on 8 movement metrics. This enables risk-based prioritization by identifying which behavioral "archetype" each person matches.</p>
      </div>
      <div class="two-col">
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #4F46E5;">CLUSTER TYPES</div>
            <table>
              <tr><th>Cluster</th><th>Description</th><th>Priority</th></tr>
              <tr style="background: #DCFCE7;"><td><strong>Routine Stable</strong></td><td>Predictable patterns, same places/times</td><td>Low</td></tr>
              <tr style="background: #DBEAFE;"><td><strong>Mobile Regular</strong></td><td>High travel but predictable routes</td><td>Normal</td></tr>
              <tr style="background: #FEF3C7;"><td><strong>Irregular Patterns</strong></td><td>Unpredictable schedule/locations</td><td>Elevated</td></tr>
              <tr style="background: #EDE9FE;"><td><strong>Night Active</strong></td><td>Most activity 10pm-6am</td><td>Review</td></tr>
              <tr style="background: #FEE2E2;"><td><strong>High-Risk Exposure</strong></td><td>Frequent flagged location visits</td><td>High</td></tr>
            </table>
          </div>
          <div class="section">
            <div class="section-title" style="background: #4F46E5;">8 BEHAVIORAL FEATURES</div>
            <table style="font-size: 9px;">
              <tr><th>Feature</th><th>What It Measures</th></tr>
              <tr><td><strong>Avg Daily Distance</strong></td><td>Miles traveled per day</td></tr>
              <tr><td><strong>Unique Locations/Week</strong></td><td>Location diversity</td></tr>
              <tr><td><strong>Routine Adherence</strong></td><td>Consistency with patterns (0-100%)</td></tr>
              <tr><td><strong>Night Activity Ratio</strong></td><td>% of activity 10pm-6am</td></tr>
              <tr><td><strong>Weekend Activity Ratio</strong></td><td>% of activity Sat/Sun</td></tr>
              <tr><td><strong>High-Risk Zone Affinity</strong></td><td>Risk zone visit frequency</td></tr>
              <tr><td><strong>Velocity Variance</strong></td><td>Movement speed variation</td></tr>
              <tr><td><strong>Dwell Time Entropy</strong></td><td>Time distribution across locations</td></tr>
            </table>
          </div>
        </div>
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #4F46E5;">QUALITY METRICS</div>
            <div class="metric-box" style="border-left-color: #4F46E5;">
              <div class="metric-title" style="color: #4F46E5;">Silhouette Score (-1 to 1)</div>
              How well the person fits their cluster. Higher = stronger match.
              <table style="font-size: 9px; margin-top: 5px;">
                <tr><td>> 0.5</td><td>Strong cluster fit</td></tr>
                <tr><td>0.25 - 0.5</td><td>Moderate fit</td></tr>
                <tr><td>0 - 0.25</td><td>Weak fit, borderline</td></tr>
                <tr><td>< 0</td><td>May belong in different cluster</td></tr>
              </table>
            </div>
            <div class="metric-box" style="border-left-color: #4F46E5;">
              <div class="metric-title" style="color: #4F46E5;">Distance to Centroid</div>
              How typical for their cluster. Lower = more typical, higher = outlier.
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #4F46E5;">BUSINESS BENEFITS</div>
            <ul class="benefits-list">
              <li><strong>Risk Prioritization:</strong> Focus on high-risk clusters first</li>
              <li><strong>Early Warning:</strong> Detect cluster changes before problems</li>
              <li><strong>Efficiency Gains:</strong> 60-80% reduction in manual review</li>
              <li><strong>Peer Comparison:</strong> Compare to similar subjects</li>
              <li><strong>Scalability:</strong> Handle large populations automatically</li>
            </ul>
          </div>
          <div class="section">
            <div class="section-title" style="background: #4F46E5;">WHEN TO ESCALATE</div>
            <div style="background: #FEF2F2; border: 1px solid #FECACA; border-radius: 4px; padding: 8px; font-size: 10px;">
              <ul style="margin-left: 15px;">
                <li>Cluster change from Stable → Irregular or High-Risk</li>
                <li>Significant drop in silhouette score</li>
                <li>Increasing high-risk zone affinity over time</li>
                <li>Sudden increase in night activity ratio</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    `,
  },
  heatmap: {
    title: 'Feature 1.6: Behavioral Heatmaps',
    subtitle: 'H3 Hexagonal Activity Visualization',
    color: '#0D9488',
    content: `
      <div class="section">
        <div class="section-title" style="background: #0D9488;">WHAT IT DOES</div>
        <p>Behavioral Heatmaps visualize geographic activity concentration using <strong>H3 hexagonal grid cells</strong>. This reveals "hotspots" where the subject spends most time, enabling quick identification of key activity areas and changes in geographic patterns over time.</p>
      </div>
      <div class="two-col">
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #0D9488;">H3 HEXAGONAL GRID</div>
            <div style="background: #F0FDFA; border: 1px solid #99F6E4; border-radius: 4px; padding: 10px; text-align: center;">
              <div style="font-size: 9px; color: #666;">Each hexagon represents activity in a geographic cell. Color intensity indicates activity level.</div>
            </div>
            <p style="margin-top: 8px; font-size: 10px;"><strong>Why Hexagons?</strong> Uniform distance to neighbors (unlike squares), better circular patterns, no edge artifacts.</p>
          </div>
          <div class="section">
            <div class="section-title" style="background: #0D9488;">RESOLUTION LEVELS</div>
            <table>
              <tr><th>Resolution</th><th>Cell Size</th><th>Best For</th></tr>
              <tr><td><strong>7</strong></td><td>~5 km</td><td>Regional overview</td></tr>
              <tr style="background: #F0FDFA;"><td><strong>8</strong> (Default)</td><td>~1 km</td><td>City-level, neighborhoods</td></tr>
              <tr><td><strong>9</strong></td><td>~350 m</td><td>Street-level detail</td></tr>
              <tr><td><strong>10</strong></td><td>~100 m</td><td>Building-level precision</td></tr>
            </table>
          </div>
          <div class="section">
            <div class="section-title" style="background: #0D9488;">COLOR SCALE</div>
            <div style="display: flex; height: 16px; border-radius: 4px; overflow: hidden; margin: 8px 0;">
              <div style="flex: 1; background: #3B82F6;"></div>
              <div style="flex: 1; background: #60A5FA;"></div>
              <div style="flex: 1; background: #FBBF24;"></div>
              <div style="flex: 1; background: #F97316;"></div>
              <div style="flex: 1; background: #EF4444;"></div>
              <div style="flex: 1; background: #DC2626;"></div>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 9px;"><span>Low Activity</span><span>Medium</span><span>High (Hotspot)</span></div>
          </div>
        </div>
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #0D9488;">CELL METRICS</div>
            <div class="metric-box" style="border-left-color: #0D9488;"><div class="metric-title" style="color: #0D9488;">Activity Count</div>Number of GPS points/check-ins in this cell.</div>
            <div class="metric-box" style="border-left-color: #0D9488;"><div class="metric-title" style="color: #0D9488;">Time-Weighted Score (0-1)</div>Recency-adjusted intensity. Recent activity weighs more.</div>
            <div class="metric-box" style="border-left-color: #0D9488;"><div class="metric-title" style="color: #0D9488;">Visit Count</div>Distinct visits (separated by leaving/returning).</div>
            <div class="metric-box" style="border-left-color: #0D9488;"><div class="metric-title" style="color: #0D9488;">Dwell Time</div>Total time spent in this cell.</div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #0D9488;">WHAT TO LOOK FOR</div>
            <div style="background: #F0FDFA; border: 1px solid #99F6E4; border-radius: 4px; padding: 8px; font-size: 10px;">
              <ul style="margin-left: 15px;">
                <li><strong>Expected hotspots:</strong> Home and work should be darkest</li>
                <li><strong>Unexpected hotspots:</strong> Investigate unknown frequent locations</li>
                <li><strong>New hotspots:</strong> Recently emerged activity areas</li>
                <li><strong>Fading hotspots:</strong> Previously frequent areas no longer visited</li>
                <li><strong>Corridor patterns:</strong> Travel routes between locations</li>
              </ul>
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #0D9488;">BUSINESS BENEFITS</div>
            <ul class="benefits-list">
              <li><strong>Visual Pattern Recognition:</strong> Instantly see activity concentration</li>
              <li><strong>Change Detection:</strong> Compare time periods for new areas</li>
              <li><strong>Home/Work Verification:</strong> Confirm addresses match hotspots</li>
              <li><strong>Investigation Support:</strong> Identify undisclosed frequent locations</li>
            </ul>
          </div>
        </div>
      </div>
    `,
  },
  gps: {
    title: 'Feature 2.1: GPS Quality Pipeline',
    subtitle: 'Real-Time GPS Processing & Spoof Detection',
    color: '#0891B2',
    content: `
      <div class="section">
        <div class="section-title" style="background: #0891B2;">WHAT IT DOES</div>
        <p>The GPS Quality Pipeline processes incoming GPS telemetry through a <strong>multi-stage validation system</strong> including quality validation, Kalman smoothing for noise reduction, and spoofing detection. It ensures only high-quality, legitimate location data enters the analytics system.</p>
      </div>
      <div class="two-col">
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #0891B2;">PROCESSING PIPELINE</div>
            <div style="background: #ECFEFF; border: 1px solid #A5F3FC; border-radius: 4px; padding: 10px; margin-bottom: 10px;">
              <div style="text-align: center; margin-bottom: 8px;"><strong>4-Stage Processing</strong></div>
              <div style="display: flex; justify-content: space-around; text-align: center; font-size: 10px;">
                <div><div style="background: #06B6D4; color: white; padding: 3px 8px; border-radius: 3px; margin-bottom: 3px;">1</div>Quality<br/>Validation</div>
                <div style="color: #0891B2;">→</div>
                <div><div style="background: #06B6D4; color: white; padding: 3px 8px; border-radius: 3px; margin-bottom: 3px;">2</div>Kalman<br/>Smoothing</div>
                <div style="color: #0891B2;">→</div>
                <div><div style="background: #06B6D4; color: white; padding: 3px 8px; border-radius: 3px; margin-bottom: 3px;">3</div>Spoof<br/>Detection</div>
                <div style="color: #0891B2;">→</div>
                <div><div style="background: #06B6D4; color: white; padding: 3px 8px; border-radius: 3px; margin-bottom: 3px;">4</div>Enrichment<br/>& Storage</div>
              </div>
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #0891B2;">QUALITY THRESHOLDS</div>
            <table>
              <tr><th>Metric</th><th>Threshold</th><th>Impact</th></tr>
              <tr style="background: #DCFCE7;"><td><strong>Satellites</strong></td><td>≥ 4</td><td>Below = low confidence</td></tr>
              <tr style="background: #DCFCE7;"><td><strong>HDOP</strong></td><td>≤ 5.0</td><td>Above = poor precision</td></tr>
              <tr style="background: #FEF3C7;"><td><strong>Accuracy</strong></td><td>≤ 50m</td><td>Above = location rejected</td></tr>
              <tr style="background: #FEF3C7;"><td><strong>Battery</strong></td><td>≥ 20%</td><td>Below = warning flag</td></tr>
            </table>
          </div>
          <div class="section">
            <div class="section-title" style="background: #0891B2;">KALMAN SMOOTHING</div>
            <div class="metric-box" style="border-left-color: #0891B2;">
              <div class="metric-title" style="color: #0891B2;">What It Does</div>
              A 2D Kalman filter reduces GPS "jitter" by combining new measurements with previous estimates, producing smoother, more accurate coordinate tracks.
            </div>
            <div style="font-size: 10px; margin-top: 8px;">
              <strong>Benefits:</strong>
              <ul style="margin-left: 15px;">
                <li>Reduces random noise in GPS signals</li>
                <li>Improves accuracy in poor signal areas</li>
                <li>Provides velocity and heading estimates</li>
              </ul>
            </div>
          </div>
        </div>
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #0891B2;">SPOOF DETECTION</div>
            <table>
              <tr><th>Indicator</th><th>Threshold</th><th>Severity</th></tr>
              <tr style="background: #FECACA;"><td><strong>Impossible Velocity</strong></td><td>> 500 mph</td><td>Critical</td></tr>
              <tr style="background: #FED7AA;"><td><strong>Suspicious Velocity</strong></td><td>> 200 mph</td><td>High</td></tr>
              <tr style="background: #FED7AA;"><td><strong>Location Jump</strong></td><td>> 100 miles</td><td>High</td></tr>
              <tr style="background: #FECACA;"><td><strong>Mock Location Flag</strong></td><td>Android API</td><td>Critical</td></tr>
              <tr style="background: #FEF3C7;"><td><strong>Perfect Accuracy</strong></td><td>0.0m exactly</td><td>Medium</td></tr>
            </table>
            <div class="metric-box" style="border-left-color: #0891B2; margin-top: 10px;">
              <div class="metric-title" style="color: #0891B2;">Recommendation Actions</div>
              <table style="font-size: 9px; margin-top: 5px;">
                <tr style="background: #DCFCE7;"><td><strong>Accept</strong></td><td>Spoof probability < 30%</td></tr>
                <tr style="background: #FEF3C7;"><td><strong>Flag</strong></td><td>Spoof probability 30-70%</td></tr>
                <tr style="background: #FECACA;"><td><strong>Reject</strong></td><td>Spoof probability > 70%</td></tr>
              </table>
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #0891B2;">KEY OUTPUTS</div>
            <div class="metric-box" style="border-left-color: #0891B2;"><div class="metric-title" style="color: #0891B2;">Quality Score (0-100%)</div>Composite score based on accuracy, satellites, HDOP, and signal quality.</div>
            <div class="metric-box" style="border-left-color: #0891B2;"><div class="metric-title" style="color: #0891B2;">Spoof Risk (0-100%)</div>Probability that the location data has been tampered with.</div>
            <div class="metric-box" style="border-left-color: #0891B2;"><div class="metric-title" style="color: #0891B2;">Smoothed Coordinates</div>Kalman-filtered lat/lon with reduced noise.</div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #0891B2;">BUSINESS BENEFITS</div>
            <ul class="benefits-list">
              <li><strong>Data Integrity:</strong> Only verified GPS data enters the system</li>
              <li><strong>Fraud Detection:</strong> Catches GPS spoofing attempts</li>
              <li><strong>Improved Accuracy:</strong> Kalman smoothing reduces errors</li>
              <li><strong>Real-Time Processing:</strong> Immediate quality assessment</li>
              <li><strong>Audit Trail:</strong> All quality checks documented</li>
            </ul>
          </div>
        </div>
      </div>
    `,
  },
  spoof: {
    title: 'Feature 2.2: GPS Spoof Detection',
    subtitle: 'Tamper Detection & Location Integrity',
    color: '#DC2626',
    content: `
      <div class="section">
        <div class="section-title" style="background: #DC2626;">WHAT IT DOES</div>
        <p>GPS Spoof Detection identifies attempts to <strong>fake or manipulate location data</strong> using multiple detection algorithms. It analyzes velocity patterns, location jumps, device signals, and behavioral inconsistencies to flag suspicious GPS data before it corrupts analytics.</p>
      </div>
      <div class="two-col">
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #DC2626;">SPOOF INDICATORS</div>
            <table>
              <tr><th>Indicator</th><th>Detection</th><th>Severity</th></tr>
              <tr style="background: #FECACA;"><td><strong>Impossible Velocity</strong></td><td>> 500 mph between points</td><td>Critical</td></tr>
              <tr style="background: #FED7AA;"><td><strong>Suspicious Velocity</strong></td><td>> 200 mph between points</td><td>High</td></tr>
              <tr style="background: #FED7AA;"><td><strong>Teleportation</strong></td><td>> 100 miles instantly</td><td>High</td></tr>
              <tr style="background: #FECACA;"><td><strong>Mock Location Flag</strong></td><td>Android API detection</td><td>Critical</td></tr>
              <tr style="background: #FEF3C7;"><td><strong>Perfect Accuracy</strong></td><td>0.0m (synthetic)</td><td>Medium</td></tr>
              <tr style="background: #FEF3C7;"><td><strong>No Satellites</strong></td><td>0 satellites reported</td><td>Medium</td></tr>
              <tr style="background: #DCFCE7;"><td><strong>Constant Coordinates</strong></td><td>Identical lat/lon</td><td>Low</td></tr>
            </table>
          </div>
          <div class="section">
            <div class="section-title" style="background: #DC2626;">RECOMMENDATION ACTIONS</div>
            <table>
              <tr><th>Recommendation</th><th>Spoof Probability</th><th>Action</th></tr>
              <tr style="background: #DCFCE7;"><td><strong>Accept</strong></td><td>< 30%</td><td>Process normally, log quality metrics</td></tr>
              <tr style="background: #FEF3C7;"><td><strong>Flag</strong></td><td>30-70%</td><td>Store with warning, review periodically</td></tr>
              <tr style="background: #FECACA;"><td><strong>Reject</strong></td><td>> 70%</td><td>Block from analytics, alert agent</td></tr>
            </table>
          </div>
          <div class="section">
            <div class="section-title" style="background: #DC2626;">DETECTION METHODS</div>
            <div style="background: #FEF2F2; border: 1px solid #FECACA; border-radius: 4px; padding: 10px;">
              <div style="text-align: center; margin-bottom: 8px;"><strong>Multi-Layer Analysis</strong></div>
              <div style="display: flex; justify-content: space-around; text-align: center; font-size: 10px;">
                <div><div style="background: #DC2626; color: white; padding: 3px 8px; border-radius: 3px; margin-bottom: 3px;">1</div>Velocity<br/>Analysis</div>
                <div><div style="background: #DC2626; color: white; padding: 3px 8px; border-radius: 3px; margin-bottom: 3px;">2</div>Signal<br/>Quality</div>
                <div><div style="background: #DC2626; color: white; padding: 3px 8px; border-radius: 3px; margin-bottom: 3px;">3</div>Device<br/>Flags</div>
                <div><div style="background: #DC2626; color: white; padding: 3px 8px; border-radius: 3px; margin-bottom: 3px;">4</div>Pattern<br/>Analysis</div>
              </div>
            </div>
          </div>
        </div>
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #DC2626;">RISK SCORE INTEGRATION</div>
            <div class="metric-box" style="border-left-color: #DC2626;">
              <div class="metric-title" style="color: #DC2626;">Check-In Risk Contribution</div>
              Spoof probability adds <strong>0-20 points</strong> to check-in risk score. Formula: probability × 20.
              <table style="font-size: 9px; margin-top: 5px;">
                <tr><td>30% spoof probability</td><td>+6 risk points</td></tr>
                <tr><td>50% spoof probability</td><td>+10 risk points</td></tr>
                <tr><td>70% spoof probability</td><td>+14 risk points</td></tr>
                <tr><td>90% spoof probability</td><td>+18 risk points</td></tr>
              </table>
            </div>
            <div class="metric-box" style="border-left-color: #DC2626;">
              <div class="metric-title" style="color: #DC2626;">Risk Alert Trigger</div>
              When recommendation = "reject" or probability > 70%, check-in is flagged for manual review (CheckInRiskEvaluationPending = true).
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #DC2626;">WHEN TO ESCALATE</div>
            <div style="background: #FEF2F2; border: 1px solid #FECACA; border-radius: 4px; padding: 8px; font-size: 10px;">
              <ul style="margin-left: 15px;">
                <li>Multiple "reject" recommendations in 24 hours</li>
                <li>Impossible velocity detected (> 500 mph)</li>
                <li>Mock location flag from device</li>
                <li>Repeated teleportation events</li>
                <li>Spoof probability trending upward over time</li>
              </ul>
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #DC2626;">BUSINESS BENEFITS</div>
            <ul class="benefits-list">
              <li><strong>Fraud Prevention:</strong> Catches fake location submissions</li>
              <li><strong>Data Integrity:</strong> Prevents corrupted data in analytics</li>
              <li><strong>Evidence Collection:</strong> Documents spoof attempts</li>
              <li><strong>Compliance Support:</strong> Validates location check-ins</li>
              <li><strong>Risk Quantification:</strong> Objective spoof probability</li>
            </ul>
          </div>
          <div class="section">
            <div class="section-title" style="background: #DC2626;">COMMON SPOOFING METHODS</div>
            <table style="font-size: 9px;">
              <tr><th>Method</th><th>Detection</th></tr>
              <tr><td>GPS Spoofing Apps</td><td>Mock location flag, perfect accuracy</td></tr>
              <tr><td>Location Simulation</td><td>Impossible velocity, teleportation</td></tr>
              <tr><td>VPN/Proxy</td><td>IP vs GPS mismatch (if available)</td></tr>
              <tr><td>Emulators</td><td>Device fingerprint anomalies</td></tr>
            </table>
          </div>
        </div>
      </div>
    `,
  },
  movement: {
    title: 'Feature 2.5: Movement Analytics',
    subtitle: 'Real-Time Speed, Heading & Trajectory Analysis',
    color: '#0284C7',
    content: `
      <div class="section">
        <div class="section-title" style="background: #0284C7;">WHAT IT DOES</div>
        <p>Movement Analytics provides <strong>real-time analysis of GPS trajectories</strong> to determine current speed, heading, movement state, and travel statistics. It detects stationary periods, calculates daily distance traveled, and alerts on suspicious velocity patterns.</p>
      </div>
      <div class="two-col">
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #0284C7;">MOVEMENT STATES</div>
            <table>
              <tr><th>State</th><th>Speed Range</th><th>Icon</th></tr>
              <tr style="background: #F3F4F6;"><td><strong>Stationary</strong></td><td>< 0.5 mph</td><td>&#x23F8;</td></tr>
              <tr style="background: #DCFCE7;"><td><strong>Walking</strong></td><td>0.5 - 4 mph</td><td>&#x1F6B6;</td></tr>
              <tr style="background: #FEF3C7;"><td><strong>Running</strong></td><td>4 - 8 mph</td><td>&#x1F3C3;</td></tr>
              <tr style="background: #DBEAFE;"><td><strong>Driving</strong></td><td>8 - 65 mph</td><td>&#x1F697;</td></tr>
              <tr style="background: #FED7AA;"><td><strong>Fast Driving</strong></td><td>65 - 85 mph</td><td>&#x1F698;</td></tr>
              <tr style="background: #FEE2E2;"><td><strong>Suspicious</strong></td><td>85 - 120 mph</td><td>&#x26A0;</td></tr>
              <tr style="background: #FECACA;"><td><strong>Impossible</strong></td><td>> 500 mph</td><td>&#x274C;</td></tr>
            </table>
          </div>
          <div class="section">
            <div class="section-title" style="background: #0284C7;">VELOCITY CALCULATION</div>
            <div class="metric-box" style="border-left-color: #0284C7;">
              <div class="metric-title" style="color: #0284C7;">Haversine Formula</div>
              <p>Speed calculated using great-circle distance between consecutive GPS points divided by time interval.</p>
              <p style="font-family: monospace; font-size: 9px; margin-top: 5px;">
                speed = distance(p1, p2) / time_delta
              </p>
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #0284C7;">HEADING CALCULATION</div>
            <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 5px; text-align: center; font-size: 10px;">
              <div><strong>&#x2191;</strong><br/>N (0°)</div>
              <div><strong>&#x2197;</strong><br/>NE (45°)</div>
              <div><strong>&#x2192;</strong><br/>E (90°)</div>
              <div><strong>&#x2198;</strong><br/>SE (135°)</div>
              <div><strong>&#x2193;</strong><br/>S (180°)</div>
              <div><strong>&#x2199;</strong><br/>SW (225°)</div>
              <div><strong>&#x2190;</strong><br/>W (270°)</div>
              <div><strong>&#x2196;</strong><br/>NW (315°)</div>
            </div>
          </div>
        </div>
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #0284C7;">DAILY STATISTICS</div>
            <div class="metric-box" style="border-left-color: #0284C7;">
              <div class="metric-title" style="color: #0284C7;">Tracked Metrics</div>
              <table style="font-size: 9px; margin-top: 5px;">
                <tr><td><strong>Distance Today</strong></td><td>Total miles traveled in period</td></tr>
                <tr><td><strong>Average Speed</strong></td><td>Mean of all valid velocities</td></tr>
                <tr><td><strong>Max Speed</strong></td><td>Peak velocity detected</td></tr>
                <tr><td><strong>Stationary Duration</strong></td><td>Time at same location (< 50m)</td></tr>
                <tr><td><strong>Points Analyzed</strong></td><td>GPS points in analysis window</td></tr>
              </table>
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #0284C7;">VELOCITY ALERTS</div>
            <table>
              <tr><th>Alert Type</th><th>Condition</th><th>Severity</th></tr>
              <tr style="background: #FEF3C7;"><td>High Speed</td><td>Above threshold</td><td>Warning</td></tr>
              <tr style="background: #FEE2E2;"><td>Suspicious</td><td>> 85 mph</td><td>Critical</td></tr>
              <tr style="background: #FECACA;"><td>Impossible</td><td>> 500 mph</td><td>Critical (Spoof)</td></tr>
            </table>
          </div>
          <div class="section">
            <div class="section-title" style="background: #0284C7;">BUSINESS BENEFITS</div>
            <ul class="benefits-list">
              <li><strong>Real-Time Awareness:</strong> Know current movement state</li>
              <li><strong>Speed Monitoring:</strong> Detect reckless driving</li>
              <li><strong>Stationary Detection:</strong> Identify prolonged stops</li>
              <li><strong>Travel Analysis:</strong> Daily distance and patterns</li>
              <li><strong>Spoof Detection:</strong> Flag impossible velocities</li>
            </ul>
          </div>
          <div class="section">
            <div class="section-title" style="background: #0284C7;">USE CASES</div>
            <div style="background: #F0F9FF; border: 1px solid #BAE6FD; border-radius: 4px; padding: 8px; font-size: 10px;">
              <ul style="margin-left: 15px;">
                <li>Detect flight risk behavior (airport approach)</li>
                <li>Monitor for unauthorized vehicle use</li>
                <li>Identify erratic movement patterns</li>
                <li>Validate check-in location authenticity</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    `,
  },
  smartgeo: {
    title: 'Feature 2.3: Smart Geofencing',
    subtitle: 'Auto-Detected Home/Work/Frequent Location Zones',
    color: '#059669',
    content: `
      <div class="section">
        <div class="section-title" style="background: #059669;">WHAT IT DOES</div>
        <p>Smart Geofencing <strong>automatically creates geofence boundaries</strong> from Pattern of Life significant locations. It identifies home, work, and frequent locations using clustering algorithms, then generates compliance-checking zones with adaptive radius and time-based monitoring.</p>
      </div>
      <div class="two-col">
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #059669;">LOCATION DETECTION</div>
            <table>
              <tr><th>Type</th><th>Detection Criteria</th><th>Icon</th></tr>
              <tr style="background: #DCFCE7;"><td><strong>Home</strong></td><td>Highest night presence (10pm-6am), > 4 nights</td><td style="font-size: 18px;">&#x1F3E0;</td></tr>
              <tr style="background: #DBEAFE;"><td><strong>Work</strong></td><td>Weekday daytime (9am-5pm), > 3 weekdays</td><td style="font-size: 18px;">&#x1F3E2;</td></tr>
              <tr style="background: #FEF3C7;"><td><strong>Frequent</strong></td><td>10+ visits with 30+ min average dwell</td><td style="font-size: 18px;">&#x1F4CD;</td></tr>
            </table>
          </div>
          <div class="section">
            <div class="section-title" style="background: #059669;">RADIUS CALCULATION</div>
            <div class="metric-box" style="border-left-color: #059669;">
              <div class="metric-title" style="color: #059669;">Adaptive Radius Formula</div>
              <p>Each geofence radius is calculated from the detected location cluster, then adjusted:</p>
              <table style="font-size: 9px; margin-top: 5px;">
                <tr><th>Type</th><th>Multiplier</th><th>Minimum</th></tr>
                <tr><td>Home</td><td>radius × 1.2</td><td>100m</td></tr>
                <tr><td>Work</td><td>radius × 1.5</td><td>150m</td></tr>
                <tr><td>Frequent</td><td>radius × 1.3</td><td>100m</td></tr>
              </table>
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #059669;">DEFAULT ACTIVE HOURS</div>
            <table>
              <tr><th>Type</th><th>Hours</th><th>Days</th></tr>
              <tr style="background: #DCFCE7;"><td>Home</td><td>6pm - 8am</td><td>Every day</td></tr>
              <tr style="background: #DBEAFE;"><td>Work</td><td>8am - 6pm</td><td>Mon-Fri</td></tr>
              <tr style="background: #FEF3C7;"><td>Frequent</td><td>All hours</td><td>Every day</td></tr>
            </table>
          </div>
        </div>
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #059669;">COMPLIANCE CHECKING</div>
            <div class="metric-box" style="border-left-color: #059669;">
              <div class="metric-title" style="color: #059669;">Real-Time Location Compliance</div>
              <p>Check if person is at expected geofence based on current time:</p>
              <table style="font-size: 9px; margin-top: 5px;">
                <tr><th>Status</th><th>Meaning</th></tr>
                <tr style="background: #DCFCE7;"><td>At Home</td><td>Inside home geofence</td></tr>
                <tr style="background: #DBEAFE;"><td>At Work</td><td>Inside work geofence</td></tr>
                <tr style="background: #FEF3C7;"><td>At Known Location</td><td>Inside frequent geofence</td></tr>
                <tr style="background: #FEE2E2;"><td>Outside Expected</td><td>Not at expected location</td></tr>
                <tr><td>Outside All</td><td>Unknown location (no expectation)</td></tr>
              </table>
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #059669;">ALERT SETTINGS</div>
            <table>
              <tr><th>Type</th><th>Alert On</th><th>Purpose</th></tr>
              <tr><td>Home</td><td>Exit</td><td>Unexpected departure</td></tr>
              <tr><td>Work</td><td>None</td><td>Informational only</td></tr>
              <tr><td>Restricted</td><td>Enter</td><td>Prohibited area entry</td></tr>
            </table>
          </div>
          <div class="section">
            <div class="section-title" style="background: #059669;">HOME MISMATCH DETECTION</div>
            <div style="background: #FEF3C7; border: 1px solid #FCD34D; border-radius: 4px; padding: 8px; font-size: 10px;">
              <strong>&#x26A0; Address Verification</strong>
              <p style="margin-top: 4px;">If registered home address is provided, system compares it to detected home location. Mismatch > 500m triggers warning for potential:</p>
              <ul style="margin-left: 15px; margin-top: 4px;">
                <li>Incorrect address on file</li>
                <li>Subject moved without reporting</li>
                <li>Possible fraud/misrepresentation</li>
              </ul>
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #059669;">BUSINESS BENEFITS</div>
            <ul class="benefits-list">
              <li><strong>Automatic Setup:</strong> No manual geofence configuration</li>
              <li><strong>Adaptive Learning:</strong> Radius adjusts to actual patterns</li>
              <li><strong>Time-Based Rules:</strong> Context-aware compliance</li>
              <li><strong>Address Verification:</strong> Detects unreported moves</li>
              <li><strong>Risk Integration:</strong> Feeds into check-in risk scoring</li>
            </ul>
          </div>
        </div>
      </div>
    `,
  },
  network: {
    title: 'Part 3: Association & Network Intelligence',
    subtitle: 'Co-location Analysis & Social Network Mapping',
    color: '#06B6D4',
    content: `
      <div class="section">
        <div class="section-title" style="background: #06B6D4;">WHAT IT DOES</div>
        <p>Network Intelligence analyzes <strong>co-location patterns</strong> to identify associations between monitored subjects. It builds a social network graph from people who appear at the same place at the same time, detects community clusters, calculates network influence metrics, and assesses association-based risk.</p>
      </div>
      <div class="two-col">
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #06B6D4;">CO-LOCATION DETECTION</div>
            <div class="metric-box" style="border-left-color: #06B6D4;">
              <div class="metric-title" style="color: #06B6D4;">How Connections Are Found</div>
              <p>Two people are considered "co-located" when they are:</p>
              <table style="font-size: 9px; margin-top: 5px;">
                <tr><th>Parameter</th><th>Default</th><th>Meaning</th></tr>
                <tr><td><strong>Time Window</strong></td><td>1 hour</td><td>Within same hour</td></tr>
                <tr><td><strong>Location Precision</strong></td><td>~1 km</td><td>Within ~1km area</td></tr>
                <tr><td><strong>Min Interactions</strong></td><td>1</td><td>At least 1 co-location</td></tr>
              </table>
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #06B6D4;">NETWORK METRICS EXPLAINED</div>
            <table>
              <tr><th>Metric</th><th>What It Means</th><th>Significance</th></tr>
              <tr><td><strong>Depth</strong></td><td>Hops from subject</td><td>1 = direct contact, 2+ = indirect</td></tr>
              <tr><td><strong>Connections</strong></td><td>Total links to others</td><td>High = social hub</td></tr>
              <tr><td><strong>Community</strong></td><td>Cluster group ID</td><td>Same # = travel together</td></tr>
              <tr><td><strong>Strength</strong></td><td>Co-location frequency</td><td>High = frequent contact</td></tr>
            </table>
          </div>
          <div class="section">
            <div class="section-title" style="background: #06B6D4;">COMMUNITY DETECTION</div>
            <div class="metric-box" style="border-left-color: #06B6D4;">
              <div class="metric-title" style="color: #06B6D4;">Louvain Algorithm</div>
              <p>Automatically groups people into communities based on connection density. People in the same community are more connected to each other than to outsiders.</p>
              <ul style="margin-left: 15px; font-size: 10px; margin-top: 5px;">
                <li><strong>Same Community:</strong> Likely travel together, share locations</li>
                <li><strong>Different Communities:</strong> Separate social circles</li>
                <li><strong>Bridge Nodes:</strong> People connecting different communities</li>
              </ul>
            </div>
          </div>
        </div>
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #06B6D4;">CENTRALITY METRICS</div>
            <table>
              <tr><th>Metric</th><th>Description</th><th>High Value Means</th></tr>
              <tr style="background: #ECFEFF;"><td><strong>Degree</strong></td><td>% of network connected to</td><td>Many direct contacts</td></tr>
              <tr><td><strong>Betweenness</strong></td><td>On shortest paths between others</td><td>"Bridge" between groups</td></tr>
              <tr style="background: #ECFEFF;"><td><strong>PageRank</strong></td><td>Importance via connections</td><td>Connected to important people</td></tr>
              <tr><td><strong>Eigenvector</strong></td><td>Influence quality</td><td>Quality over quantity</td></tr>
            </table>
            <div style="background: #ECFEFF; border: 1px solid #A5F3FC; border-radius: 4px; padding: 8px; margin-top: 8px; font-size: 10px;">
              <strong>Interpretation:</strong> High betweenness + low degree = broker/coordinator. High degree + high PageRank = central hub.
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #06B6D4;">ASSOCIATION RISK SCORING</div>
            <div class="metric-box" style="border-left-color: #06B6D4;">
              <div class="metric-title" style="color: #06B6D4;">Risk Factors (0-100%)</div>
              <table style="font-size: 9px; margin-top: 5px;">
                <tr><th>Factor</th><th>Weight</th><th>Trigger</th></tr>
                <tr><td>High-risk connections</td><td>up to 40%</td><td>Links to flagged individuals</td></tr>
                <tr><td>Community risk level</td><td>up to 30%</td><td>High-risk members in cluster</td></tr>
                <tr><td>Network position</td><td>up to 30%</td><td>High centrality scores</td></tr>
              </table>
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #06B6D4;">INVESTIGATION FEATURES</div>
            <ul class="benefits-list">
              <li><strong>Hover Tooltips:</strong> See person name/status on graph nodes</li>
              <li><strong>Click to Investigate:</strong> Open side panel with full details</li>
              <li><strong>Connected Table:</strong> Sortable list of all connections</li>
              <li><strong>Profile Navigation:</strong> Jump to person's full profile</li>
              <li><strong>Pattern of Life Link:</strong> View person's location history</li>
            </ul>
          </div>
        </div>
      </div>
      <div class="section">
        <div class="section-title" style="background: #06B6D4;">WHEN TO ESCALATE</div>
        <div style="background: #FEF2F2; border: 1px solid #FECACA; border-radius: 4px; padding: 8px; font-size: 10px;">
          <ul style="margin-left: 15px; display: grid; grid-template-columns: 1fr 1fr; gap: 5px;">
            <li>Subject has multiple high-risk connections</li>
            <li>Subject is in a high-risk community cluster</li>
            <li>High betweenness (coordination role)</li>
            <li>New connections to flagged individuals</li>
            <li>Community composition change</li>
            <li>Subject becomes network "hub"</li>
          </ul>
        </div>
      </div>
      <div class="section">
        <div class="section-title" style="background: #06B6D4;">BUSINESS BENEFITS</div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 10px;">
          <div class="metric-box" style="border-left-color: #06B6D4;"><strong>Association Discovery:</strong> Find unknown connections between subjects based on location overlap</div>
          <div class="metric-box" style="border-left-color: #06B6D4;"><strong>Group Detection:</strong> Identify coordinated groups who travel/appear together</div>
          <div class="metric-box" style="border-left-color: #06B6D4;"><strong>Risk Assessment:</strong> Quantify risk based on who someone associates with</div>
          <div class="metric-box" style="border-left-color: #06B6D4;"><strong>Flight Risk Networks:</strong> Detect potential coordinated flight risk</div>
        </div>
      </div>
    `,
  },
  route: {
    title: 'Feature 2.6: Route Reconstruction & Playback',
    subtitle: 'Historical Path Visualization with Timeline Controls',
    color: '#8B5CF6',
    content: `
      <div class="section">
        <div class="section-title" style="background: #8B5CF6;">WHAT IT DOES</div>
        <p>Route Reconstruction <strong>rebuilds historical movement paths</strong> from GPS data for visual playback and analysis. It identifies stops, calculates travel statistics, and provides timeline controls for reviewing movement history over configurable time periods.</p>
      </div>
      <div class="two-col">
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #8B5CF6;">ROUTE RECONSTRUCTION</div>
            <table>
              <tr><th>Process</th><th>Description</th></tr>
              <tr><td><strong>Data Collection</strong></td><td>Gather GPS points for time period</td></tr>
              <tr><td><strong>Point Enrichment</strong></td><td>Calculate velocity, heading between points</td></tr>
              <tr><td><strong>Stop Detection</strong></td><td>Identify locations with prolonged dwell</td></tr>
              <tr><td><strong>Route Simplification</strong></td><td>RDP algorithm reduces point count</td></tr>
              <tr><td><strong>Statistics</strong></td><td>Calculate distance, duration, speeds</td></tr>
            </table>
          </div>
          <div class="section">
            <div class="section-title" style="background: #8B5CF6;">STOP DETECTION</div>
            <div class="metric-box" style="border-left-color: #8B5CF6;">
              <div class="metric-title" style="color: #8B5CF6;">Stop Identification Criteria</div>
              <table style="font-size: 9px; margin-top: 5px;">
                <tr><th>Parameter</th><th>Value</th></tr>
                <tr><td>Distance Threshold</td><td>&lt; 50 meters</td></tr>
                <tr><td>Minimum Duration</td><td>&ge; 5 minutes</td></tr>
                <tr><td>Point Count</td><td>GPS points at location</td></tr>
              </table>
              <p style="margin-top: 8px; font-size: 9px;">Stops are classified by duration:</p>
              <table style="font-size: 9px; margin-top: 5px;">
                <tr><th>Type</th><th>Duration</th><th>Icon</th></tr>
                <tr><td>Brief</td><td>&lt; 15 min</td><td>&#x23F8;</td></tr>
                <tr><td>Short</td><td>15-60 min</td><td>&#x1F4CD;</td></tr>
                <tr><td>Medium</td><td>1-4 hours</td><td>&#x1F3E0;</td></tr>
                <tr><td>Extended</td><td>&gt; 4 hours</td><td>&#x1F3E8;</td></tr>
              </table>
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #8B5CF6;">PLAYBACK CONTROLS</div>
            <table>
              <tr><th>Control</th><th>Function</th></tr>
              <tr><td>&#x25B6; <strong>Play/Pause</strong></td><td>Animate route progression</td></tr>
              <tr><td>&#x23EE; <strong>Start</strong></td><td>Jump to beginning</td></tr>
              <tr><td>&#x23ED; <strong>End</strong></td><td>Jump to end</td></tr>
              <tr><td><strong>Timeline Scrubber</strong></td><td>Drag to any point in time</td></tr>
              <tr><td><strong>Speed</strong></td><td>0.5x, 1x, 2x, 4x playback</td></tr>
            </table>
          </div>
        </div>
        <div class="col">
          <div class="section">
            <div class="section-title" style="background: #8B5CF6;">ROUTE STATISTICS</div>
            <div class="metric-box" style="border-left-color: #8B5CF6;">
              <div class="metric-title" style="color: #8B5CF6;">Tracked Metrics</div>
              <table style="font-size: 9px; margin-top: 5px;">
                <tr><td><strong>Total Distance</strong></td><td>Miles traveled in period</td></tr>
                <tr><td><strong>Total Duration</strong></td><td>Time from start to end</td></tr>
                <tr><td><strong>Moving Time</strong></td><td>Time in motion</td></tr>
                <tr><td><strong>Stopped Time</strong></td><td>Time at identified stops</td></tr>
                <tr><td><strong>Average Speed</strong></td><td>Mean of valid velocities</td></tr>
                <tr><td><strong>Max Speed</strong></td><td>Peak velocity recorded</td></tr>
                <tr><td><strong>Point Count</strong></td><td>GPS points analyzed</td></tr>
                <tr><td><strong>Stop Count</strong></td><td>Significant stops identified</td></tr>
              </table>
            </div>
          </div>
          <div class="section">
            <div class="section-title" style="background: #8B5CF6;">SPEED VISUALIZATION</div>
            <table>
              <tr><th>Color</th><th>Speed Range</th><th>State</th></tr>
              <tr style="background: #F3F4F6;"><td style="color: #6B7280;">&#x25CF; Gray</td><td>&lt; 5 mph</td><td>Stationary</td></tr>
              <tr style="background: #DCFCE7;"><td style="color: #22C55E;">&#x25CF; Green</td><td>5-25 mph</td><td>Slow</td></tr>
              <tr style="background: #DBEAFE;"><td style="color: #3B82F6;">&#x25CF; Blue</td><td>25-45 mph</td><td>Moderate</td></tr>
              <tr style="background: #FEF3C7;"><td style="color: #F59E0B;">&#x25CF; Amber</td><td>45-65 mph</td><td>Fast</td></tr>
              <tr style="background: #FED7AA;"><td style="color: #F97316;">&#x25CF; Orange</td><td>65-85 mph</td><td>Very Fast</td></tr>
              <tr style="background: #FEE2E2;"><td style="color: #EF4444;">&#x25CF; Red</td><td>&gt; 85 mph</td><td>Speeding</td></tr>
            </table>
          </div>
          <div class="section">
            <div class="section-title" style="background: #8B5CF6;">BUSINESS BENEFITS</div>
            <ul class="benefits-list">
              <li><strong>Activity Reconstruction:</strong> Review where person went</li>
              <li><strong>Stop Analysis:</strong> Identify significant locations visited</li>
              <li><strong>Speed Monitoring:</strong> Detect dangerous driving</li>
              <li><strong>Incident Investigation:</strong> Trace movement after events</li>
              <li><strong>Court Evidence:</strong> Exportable timeline data</li>
            </ul>
          </div>
          <div class="section">
            <div class="section-title" style="background: #8B5CF6;">USE CASES</div>
            <div style="background: #F5F3FF; border: 1px solid #C4B5FD; border-radius: 4px; padding: 8px; font-size: 10px;">
              <ul style="margin-left: 15px;">
                <li>Review travel before missed check-in</li>
                <li>Verify alibi claims with GPS evidence</li>
                <li>Identify patterns in movement history</li>
                <li>Reconstruct day-of activities for reports</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    `,
  },
};

const FeatureDocumentation = ({ feature, isOpen, onClose }) => {
  const doc = FEATURE_DOCS[feature];

  if (!isOpen || !doc) return null;

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${doc.title}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
          body { font-family: 'Segoe UI', Tahoma, sans-serif; font-size: 11px; line-height: 1.4; color: #333; padding: 0.5in; max-width: 8.5in; }
          @media print { body { padding: 0.25in; } * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } }
          .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid ${doc.color}; padding-bottom: 10px; margin-bottom: 15px; }
          .header h1 { color: ${doc.color}; font-size: 22px; }
          .header .subtitle { color: #666; font-size: 12px; }
          .section { margin-bottom: 15px; }
          .section-title { background: ${doc.color}; color: white; padding: 5px 10px; font-size: 12px; font-weight: bold; margin-bottom: 8px; }
          .two-col { display: flex; gap: 15px; }
          .col { flex: 1; }
          table { width: 100%; border-collapse: collapse; font-size: 10px; }
          th, td { border: 1px solid #ddd; padding: 5px 8px; text-align: left; }
          th { background: #f5f5f5; font-weight: bold; }
          .metric-box { background: #f8f9fa; border-left: 3px solid ${doc.color}; padding: 8px; margin-bottom: 8px; }
          .metric-title { font-weight: bold; color: ${doc.color}; }
          .benefits-list { list-style: none; }
          .benefits-list li { padding: 4px 0 4px 20px; position: relative; }
          .benefits-list li:before { content: "✓"; position: absolute; left: 0; color: #22C55E; font-weight: bold; }
          .footer { margin-top: 15px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 9px; color: #666; text-align: center; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1>${doc.title}</h1>
            <div class="subtitle">SafePath Analytics - ${doc.subtitle}</div>
          </div>
          <div style="text-align: right; font-size: 10px; color: #666;"><strong>CONFIDENTIAL</strong><br>Internal Use Only</div>
        </div>
        ${doc.content}
        <div class="footer">SafePath Analytics | ${doc.title} | Document Version 1.0</div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div
          className="px-6 py-4 border-b flex items-center justify-between"
          style={{ borderBottomColor: doc.color, borderBottomWidth: '3px' }}
        >
          <div>
            <h2 className="text-xl font-bold" style={{ color: doc.color }}>
              {doc.title}
            </h2>
            <p className="text-sm text-gray-500">{doc.subtitle}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print PDF
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div
          className="flex-1 overflow-auto p-6"
          style={{
            fontFamily: "'Segoe UI', Tahoma, sans-serif",
            fontSize: '11px',
            lineHeight: '1.4',
          }}
        >
          <style>{`
            .section { margin-bottom: 15px; }
            .section-title { color: white; padding: 5px 10px; font-size: 12px; font-weight: bold; margin-bottom: 8px; }
            .two-col { display: flex; gap: 15px; }
            .col { flex: 1; }
            table { width: 100%; border-collapse: collapse; font-size: 10px; }
            th, td { border: 1px solid #ddd; padding: 5px 8px; text-align: left; }
            th { background: #f5f5f5; font-weight: bold; }
            .metric-box { background: #f8f9fa; border-left: 3px solid; padding: 8px; margin-bottom: 8px; }
            .metric-title { font-weight: bold; }
            .benefits-list { list-style: none; padding: 0; }
            .benefits-list li { padding: 4px 0 4px 20px; position: relative; }
            .benefits-list li:before { content: "✓"; position: absolute; left: 0; color: #22C55E; font-weight: bold; }
          `}</style>
          <div dangerouslySetInnerHTML={{ __html: doc.content }} />
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t bg-gray-50 text-center text-xs text-gray-500">
          SafePath Analytics | {doc.title} | Document Version 1.0
        </div>
      </div>
    </div>
  );
};

/**
 * Info button component to open documentation
 */
export const FeatureInfoButton = ({ feature, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const doc = FEATURE_DOCS[feature];

  if (!doc) return null;

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-1 px-2 py-1 text-xs rounded-md hover:bg-gray-100 transition-colors"
        style={{ color: doc.color }}
        title={`View ${doc.title} documentation`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {label || 'Info'}
      </button>
      <FeatureDocumentation feature={feature} isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

export default FeatureDocumentation;
